import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";

import {
  ChannelThreadId,
  ConversationId,
  DraftMessageId,
  MacroAttachmentId,
  MacroId,
  SupportTicketId,
} from "../ids/definitions";
import {
  Attachment,
  AttachmentType,
} from "../models/shopper-ai/conversation/message/attachment";
import {
  SupportTicketSnoozeOptions,
  type SupportTicketStatus,
} from "../models/shopper-ai/conversation/support-ticket";

// Subset of SupportTicketStatus values a macro is allowed to assign. Lifecycle
// statuses (HIDDEN, ARCHIVED, DELETED, DRAFT) are intentionally excluded.
export const MACRO_STATUS_TO_SET_VALUES = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
] as const satisfies readonly SupportTicketStatus[];
export const MacroStatusToSet = Schema.Literal(...MACRO_STATUS_TO_SET_VALUES);
export type MacroStatusToSet = typeof MacroStatusToSet.Type;

export const MACRO_STATUS_TO_SET_LABELS: Record<MacroStatusToSet, string> = {
  OPEN: "Open",
  CLOSED: "Closed",
  SNOOZED: "Snoozed",
  IN_PROGRESS: "In progress",
};

export const MacroAutomationKind = {
  SET_STATUS: "setStatus",
  ADD_TAGS: "addTags",
  CHANGE_EMAIL_SUBJECT: "changeEmailSubject",
  ADD_INTERNAL_NOTE: "addInternalNote",
  FORWARD_MESSAGE: "forwardMessage",
} as const;
export type MacroAutomationKind =
  (typeof MacroAutomationKind)[keyof typeof MacroAutomationKind];
export const MacroAutomationKindSchema = Schema.Literal(
  ...Object.values(MacroAutomationKind),
);

export const NoteToAdd = Schema.Struct({
  content: Schema.String,
  htmlContent: Schema.String,
  usersMentioned: Schema.NullOr(Schema.Array(Schema.String)),
});
export type NoteToAdd = typeof NoteToAdd.Type;

export const MessageToForward = Schema.Struct({
  emailAddress: Schema.String,
  extraContent: Schema.NullOr(Schema.String),
});
export type MessageToForward = typeof MessageToForward.Type;

// Read-only attachment shape returned with a macro. `url` is the resolved
// download URL (stored public URL for reused legacy objects, or a freshly
// signed URL for private objects) — or `null` when it can't be resolved right
// now (e.g. signing failed). A null url must NOT hide the attachment: the
// editor still shows that the macro has it. Attachments are managed through
// dedicated upload/delete endpoints, not the macro write payload.
export const MacroAttachment = Schema.Struct({
  id: MacroAttachmentId,
  url: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  mimeType: Schema.NullOr(Schema.String),
  // `type: "inline"` rows are embedded in the macro body (a `cid:<contentId>`
  // reference resolved to `url` here on read), not shown as thumbnails; null is
  // a legacy thumbnail. The macro editor uses `contentId` to round-trip body
  // image URLs back to `cid:` on save.
  type: Schema.NullOr(AttachmentType),
  contentId: Schema.NullOr(Schema.String),
});
export type MacroAttachment = typeof MacroAttachment.Type;

// File is base64-encoded in the JSON payload, mirroring addDraftAttachment.
// `type`/`contentId` distinguish an inline body image from a thumbnail file;
// omitting them preserves the legacy thumbnail-upload behavior.
export const AddMacroAttachmentPayload = Schema.Struct({
  file: Schema.String,
  filename: Schema.String,
  mimeType: Schema.String,
  size: Schema.Number,
  type: Schema.optional(AttachmentType),
  contentId: Schema.optional(Schema.String),
});
export type AddMacroAttachmentPayload = typeof AddMacroAttachmentPayload.Type;

const AddMacroAttachmentResponse = Schema.Struct({
  attachment: MacroAttachment,
});

// Applies a macro's inline body images to a live draft: copies each inline macro
// attachment (S3 object) into a *draft* inline attachment with the same
// contentId, server-side, so the draft owns the image and the normal email send
// path embeds it as a `cid:` part. Returns each created draft attachment's fresh
// signed `url` keyed by `contentId` so the client can rewrite the applied body's
// `<img src>` from the macro URL to the draft URL (live preview + send match).
export const CopyMacroInlineImagesToDraftPayload = Schema.Struct({
  draftMessageId: DraftMessageId,
});
export type CopyMacroInlineImagesToDraftPayload =
  typeof CopyMacroInlineImagesToDraftPayload.Type;

const CopyMacroInlineImagesToDraftResponse = Schema.Struct({
  attachments: Schema.Array(
    Schema.Struct({ contentId: Schema.String, url: Schema.String }),
  ),
});

// Copies a macro's file attachments (chips, not inline body images) onto a draft
// server-side; the browser can't read the S3 object cross-origin.
export const CopyMacroAttachmentsToDraftPayload = Schema.Struct({
  draftMessageId: DraftMessageId,
});
export type CopyMacroAttachmentsToDraftPayload =
  typeof CopyMacroAttachmentsToDraftPayload.Type;

const CopyMacroAttachmentsToDraftResponse = Schema.Struct({
  attachments: Schema.Array(Attachment),
});

/** Pending automations from the client, reflecting any chips the agent dismissed. */
export const MacroPendingAutomations = Schema.Struct({
  statusToSet: Schema.NullOr(MacroStatusToSet),
  snoozeDuration: Schema.NullOr(SupportTicketSnoozeOptions),
  tagsToAdd: Schema.Array(Schema.String),
  emailSubjectToSet: Schema.NullOr(Schema.String),
  noteToAdd: Schema.NullOr(NoteToAdd),
});
export type MacroPendingAutomations = typeof MacroPendingAutomations.Type;

/** When `pendingAutomations` is set the server uses those values instead of the macro DB, avoiding double-apply. */
export const ApplyMacroAutomationsPayload = Schema.Struct({
  supportTicketId: SupportTicketId,
  threadId: Schema.optional(ChannelThreadId),
  pendingAutomations: Schema.optional(MacroPendingAutomations),
});
export type ApplyMacroAutomationsPayload =
  typeof ApplyMacroAutomationsPayload.Type;

export const Macro = Schema.Struct({
  id: MacroId,
  name: Schema.String,
  content: Schema.String,
  htmlContent: Schema.String,
  // Serialized Plate editor value (JSON) for the body, with inline images as
  // `cid:<contentId>` refs (resolved to signed URLs client-side via
  // `inlineAttachments`). The editor reloads from this round-trip-safe value
  // instead of re-parsing `htmlContent`. Null for legacy macros (fall back to
  // `htmlContent`).
  bodyValue: Schema.NullOr(Schema.String),
  tagsToAdd: Schema.NullOr(Schema.Array(Schema.String)),
  statusToSet: Schema.NullOr(MacroStatusToSet),
  snoozeDuration: Schema.NullOr(SupportTicketSnoozeOptions),
  emailSubjectToSet: Schema.NullOr(Schema.String),
  noteToAdd: Schema.NullOr(NoteToAdd),
  messageToForward: Schema.NullOr(MessageToForward),
  // Thumbnail files only — inline body images are excluded so they never
  // double-show as chips. `htmlContent` references inline images as `cid:` (the
  // service resolves those to fresh signed URLs on read).
  attachments: Schema.Array(MacroAttachment),
  // Inline body images (`type: "inline"`), each with its resolved `url` +
  // `contentId`. The editor uses these to round-trip body URLs back to `cid:` on
  // save; the apply path inlines their bytes into the draft.
  inlineAttachments: Schema.Array(MacroAttachment),
  createdAt: Schema.Date,
});
export type Macro = typeof Macro.Type;

const MacroWritableFields = {
  name: Schema.String,
  content: Schema.String,
  htmlContent: Schema.String,
  bodyValue: Schema.NullOr(Schema.String),
  tagsToAdd: Schema.NullOr(Schema.Array(Schema.String)),
  statusToSet: Schema.NullOr(MacroStatusToSet),
  snoozeDuration: Schema.NullOr(SupportTicketSnoozeOptions),
  emailSubjectToSet: Schema.NullOr(Schema.String),
  noteToAdd: Schema.NullOr(NoteToAdd),
  messageToForward: Schema.NullOr(MessageToForward),
};

export const CreateMacroPayload = Schema.Struct(MacroWritableFields);
export type CreateMacroPayload = typeof CreateMacroPayload.Type;

export const UpdateMacroPayload = Schema.Struct(MacroWritableFields);
export type UpdateMacroPayload = typeof UpdateMacroPayload.Type;

const MacroResponse = Schema.Struct({ macro: Macro });

const GetMacrosResponse = Schema.Struct({ macros: Schema.Array(Macro) });

/** Columns the macro list can be ordered by. */
export const MacroSortField = Schema.Literal("name", "createdAt");
export type MacroSortField = typeof MacroSortField.Type;

export const MacroSortDirection = Schema.Literal("asc", "desc");
export type MacroSortDirection = typeof MacroSortDirection.Type;

/**
 * Optional scoping for the macro list. When `supportTicketId` is provided the
 * server returns the macros for the *ticket's* account (resolved through
 * connected-team access) rather than the caller's home-team account. Omitting
 * it preserves the legacy caller-account behavior for non-ticket contexts.
 *
 * `search`, `sortField`, and `sortDirection` are applied server-side: `search`
 * is a case-insensitive substring match on the macro name, and the list is
 * ordered by `sortField` (default `name`) in `sortDirection` (default `asc`).
 */
export const GetMacrosUrlParams = Schema.Struct({
  supportTicketId: Schema.optional(SupportTicketId),
  search: Schema.optional(Schema.String),
  sortField: Schema.optional(MacroSortField),
  sortDirection: Schema.optional(MacroSortDirection),
});
export type GetMacrosUrlParams = typeof GetMacrosUrlParams.Type;

const ReplaceMacroVariablesPayloadBase = {
  content: Schema.String,
  timezone: Schema.optional(Schema.String),
};

export const ReplaceMacroVariablesPayload = Schema.Union(
  Schema.Struct({
    ...ReplaceMacroVariablesPayloadBase,
    conversationId: ConversationId,
  }),
  Schema.Struct({
    ...ReplaceMacroVariablesPayloadBase,
    supportTicketId: SupportTicketId,
  }),
);
export type ReplaceMacroVariablesPayload =
  typeof ReplaceMacroVariablesPayload.Type;

const ReplaceMacroVariablesResponse = Schema.Struct({
  message: Schema.String,
  valuesExistForAllVariables: Schema.Boolean,
});

export const MacroGroup = HttpApiGroup.make("macro")
  .add(
    endpoint.merchant
      .get("getMacros", "/macros")
      .setUrlParams(GetMacrosUrlParams)
      .addSuccess(GetMacrosResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("replaceMacroVariables", "/macros/replace-variables")

      .setPayload(ReplaceMacroVariablesPayload)
      .addSuccess(ReplaceMacroVariablesResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createMacro", "/macros")

      .setPayload(CreateMacroPayload)
      .addSuccess(MacroResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateMacro", "/macros/:id")

      .setPath(Schema.Struct({ id: MacroId }))
      .setPayload(UpdateMacroPayload)
      .addSuccess(MacroResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Conflict)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteMacro", "/macros/:id")

      .setPath(Schema.Struct({ id: MacroId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("addMacroAttachment", "/macros/:id/attachments")

      .setPath(Schema.Struct({ id: MacroId }))
      .setPayload(AddMacroAttachmentPayload)
      .addSuccess(AddMacroAttachmentResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteMacroAttachment", "/macros/:id/attachments/:attachmentId")

      .setPath(Schema.Struct({ id: MacroId, attachmentId: MacroAttachmentId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "copyMacroInlineImagesToDraft",
        "/macros/:id/inline-images/copy-to-draft",
      )
      .setPath(Schema.Struct({ id: MacroId }))
      .setPayload(CopyMacroInlineImagesToDraftPayload)
      .addSuccess(CopyMacroInlineImagesToDraftResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post(
        "copyMacroAttachmentsToDraft",
        "/macros/:id/attachments/copy-to-draft",
      )
      .setPath(Schema.Struct({ id: MacroId }))
      .setPayload(CopyMacroAttachmentsToDraftPayload)
      .addSuccess(CopyMacroAttachmentsToDraftResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("applyMacroAutomations", "/macros/:id/apply")

      .setPath(Schema.Struct({ id: MacroId }))
      .setPayload(ApplyMacroAutomationsPayload)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(HttpApiError.InternalServerError)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
